import { DataSetIntegrationFieldValidationError } from 'libs/end-to-end/src/data-sets';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { DataSetEditCompositeHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditComposite';
import { useTransformDataSetSelector } from 'apps/sigma2-ui/src/data-wizard/lib/useTransformDataSetSelector';
import React, { useCallback, useMemo } from 'react';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useParams } from 'react-router';
import { TransformEditorUI } from 'apps/sigma2-ui/src/data-wizard/components/transform-page/TransformEditorUI';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipeTypes } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { DataSetCompositeRecipeConfigurationComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/DataSetCompositeRecipeConfigurationComponent';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { useGenerateReportsInsights } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-generate-reports-insights';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { ResumeParsingCacheBusterDefaultVersion } from 'libs/end-to-end/src/types/databases/integrations/resume-parsing-cache-buster';
import { localCompositeDataSetCommand } from 'libs/end-to-end/src/local-transform/local-composite-data-set-command';
import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';

const preloadedCompositeDataSetCode = `import pandas as pd
import numpy as np
import s2utils

def run_composite_dataset(datasources, output_dir):
    ...start typing here...
`;

const preloadedAiInterviewerCode = `import pandas as pd
import numpy as np
import s2utils

def run_ai_interviewer(datasources, output_dir):
    ...start typing here...
`;

export const DataSetCompositeConfigurationComponent = ({
  compositeHook,
}: {
  compositeHook: DataSetEditCompositeHook;
  integrationFieldValidationErrors: DataSetIntegrationFieldValidationError[];
}) => {
  const { organizationId, databaseId, dataSetId } = useParams();

  if (!organizationId || !databaseId || !dataSetId) {
    throw new Error('Organization ID and Database ID are required');
  }

  const { config, setConfig } = compositeHook;

  const setTaggedDataSetReferences = useCallback(
    (references: TaggedDataSetReference[]) => {
      setConfig({
        ...config,
        taggedDataSetReferences: references,
      });
    },
    [config, setConfig],
  );

  const setCode = useCallback(
    (code: string) => {
      setConfig({
        ...config,
        pythonScriptContents: code,
      });
    },
    [config, setConfig],
  );

  const setPreloadableCode = useCallback(() => {
    setConfig({
      ...config,
      pythonScriptContents: preloadedCompositeDataSetCode,
    });
  }, [config, setConfig]);

  const setRecipe = useCallback(
    (recipe: CompositeDataSetRecipe | null) => {
      setConfig({
        ...config,
        recipe,
      });
    },
    [config, setConfig],
  );

  const setRunOrchestrationOnBatch = useCallback(
    (runOrchestrationOnBatch: boolean) => {
      setConfig({
        ...config,
        runOrchestrationOnBatch,
      });
    },
    [config, setConfig],
  );

  const setRunScriptOnBatch = useCallback(
    (runScriptOnBatch: boolean) => {
      setConfig({
        ...config,
        runScriptOnBatch,
      });
    },
    [config, setConfig],
  );

  const {
    addDataSetVersionButtonDisabled,
    addDataSetVersionButtonBehavior,
    addDataSetVersionButtonLabel,
    modal,
    heading,
    rows,
  } = useTransformDataSetSelector({
    taggedDataSetReferences: config.taggedDataSetReferences,
    setTaggedDataSetReferences,
    organizationId,
    databaseId,
    isRunningTransform: false,
  });

  const reports = useMemo(() => {
    if (!config.recipe) {
      return [];
    }

    switch (config.recipe.type) {
      case 'employee-candidate-matching':
        return config.recipe.reports;
      case 'ai-interviewer':
        return [];
      case 'common-model-validation':
        return config.recipe.reports;
      case 'rename-columns':
        return config.recipe.reports;
      case 'merge-resume-parsing':
        return [];
      case 'resume-address-parser':
        return [];
      case 'commute-times':
        return [];
      case 'merge-filtering':
        return [];
      case 'resume-fetcher':
        return [];
      case 'resume-parser':
        return [];
      case 'dagster-source':
        return [];
      default:
        throw guardExhaustive(config.recipe);
    }
  }, [config.recipe]);

  const generateReportsInsightsPayload = useGenerateReportsInsights({
    organizationId,
    databaseId,
    dataSetId,
    taggedDataSetReferences: config.taggedDataSetReferences,
    reports,
  });

  const compositeDataSetCommand = useMemo(() => {
    return localCompositeDataSetCommand({
      taggedDataSetReferences: config.taggedDataSetReferences,
      uiEnvironmentName: uiEnvironment.uiEnvironmentName,
      uiSandboxName: uiEnvironment.uiSandboxName,
      organizationId: organizationId,
      databaseId: databaseId,
      compositeDataSetId: dataSetId,
    });
  }, [config.taggedDataSetReferences, organizationId, databaseId, dataSetId]);

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <Button
        behavior={addDataSetVersionButtonBehavior}
        text={addDataSetVersionButtonLabel}
        disabled={addDataSetVersionButtonDisabled}
      />
      <AdminTable headings={heading} rows={rows} />

      <S2Checkbox
        checked={config.runOrchestrationOnBatch}
        onChange={() => {
          setRunOrchestrationOnBatch(!config.runOrchestrationOnBatch);
        }}
      >
        {'Run Orchestration on Batch'}
      </S2Checkbox>

      <S2Checkbox
        checked={config.runScriptOnBatch}
        onChange={() => {
          setRunScriptOnBatch(!config.runScriptOnBatch);
        }}
      >
        {'Run Script on Batch'}
      </S2Checkbox>

      <textarea
        className="form-textarea w-full h-32"
        value={compositeDataSetCommand}
        readOnly
      >
        Local Composite Dataset Command
      </textarea>

      <S2Checkbox
        checked={config.recipe !== null}
        onChange={() => {
          if (config.recipe !== null) {
            setRecipe(null);
          } else {
            setRecipe({
              type: 'rename-columns',
              reports: [],
              renamingReportsConfigs: [],
            });
          }
        }}
      >
        {'Use Composite Dataset Recipe'}
      </S2Checkbox>

      {config.recipe !== null && (
        <SelectBoxWithSearch
          type={'single'}
          setSelected={(recipeType) => {
            switch (recipeType) {
              case 'rename-columns':
                setRecipe({
                  type: 'rename-columns',
                  reports: [],
                  renamingReportsConfigs: [],
                });
                break;
              case 'common-model-validation':
                setRecipe({
                  type: 'common-model-validation',
                  reports: [],
                  reportsToValidate: [],
                });
                break;
              case 'ai-interviewer':
                setRecipe({
                  type: 'ai-interviewer',
                  script: preloadedAiInterviewerCode,
                  rubricsByJobTitle: [],
                  openAiModel: 'gpt-4o-mini',
                  aiInterviewerDebugMode: 'disable-debug',
                  enableHallucinationFilter: false,
                  runOnDagster: false,
                  combineAnswersInPipeline: false,
                  runBadLanguageDetector: false,
                });
                break;
              case 'employee-candidate-matching':
                setRecipe({
                  type: 'employee-candidate-matching',
                  reports: [],
                  idColumn: {
                    employeeColumn: '',
                    candidateColumn: '',
                  },
                  firstNameColumnToMatch: {
                    employeeColumn: '',
                    candidateColumn: '',
                  },
                  lastNameColumnToMatch: {
                    employeeColumn: '',
                    candidateColumn: '',
                  },
                });
                break;
              case 'merge-resume-parsing':
                setRecipe({
                  type: 'merge-resume-parsing',
                  mergeDataSetId: '',
                  employeeCandidatesFilterDataSetId: '',
                  gradingConfig: null,
                  resumeParsingCacheBusterVersion:
                    ResumeParsingCacheBusterDefaultVersion,
                  isResumeParsingFilteringDisabled: false,
                  useV2Grading: false,
                  triggeredByDagster: false,
                  applicationAppliedAtFilteringConfigs: null,
                  jobTitleSelectiveFilteringConfigs: null,
                });
                break;
              case 'resume-address-parser':
                setRecipe({
                  type: 'resume-address-parser',
                  mergeDataSetId: '',
                });
                break;
              case 'commute-times':
                setRecipe({
                  type: 'commute-times',
                  commuteTimesType: 'Candidate',
                  mergeDataSetId: '',
                });
                break;
              case 'merge-filtering':
                setRecipe({
                  type: 'merge-filtering',
                  mergeDataSetId: '',
                  applicationCurrentStageFilteringConfigs: [],
                });
                break;
              case 'resume-fetcher':
                setRecipe({
                  type: 'resume-fetcher',
                  dataSetId: '',
                  fileName: '',
                  integrationConfig: {
                    type: 'workday-candidates',
                    integrationType: 'Workday ATS',
                    workdaySoapVersion: '',
                    endpoint: '',
                    authUsernameSecretReference: {
                      secretId: '',
                    },
                    authPasswordSecretReference: {
                      secretId: '',
                    },
                    fileNamePrefix: 'workday_candidates_',
                    remoteIdColumnIndex: 0,
                  },
                });
                break;
              case 'resume-parser':
                setRecipe({
                  type: 'resume-parser',
                  dataSetId: '',
                  fileName: '',
                  resumeParsingCacheBusterVersion:
                    ResumeParsingCacheBusterDefaultVersion,
                  disableDbtJob: false,
                  useV2Grading: false,
                  gradingConfig: null,
                  writeOutputToS3: false,
                });
                break;
              case 'dagster-source':
                setRecipe({
                  type: 'dagster-source',
                  dagsterTransformId: '',
                });
                break;
              default:
                throw guardExhaustive(recipeType);
            }
          }}
          selected={config.recipe.type}
          list={CompositeDataSetRecipeTypes.map((type) => ({
            optionValue: type,
            optionLabel: type,
          }))}
          label={null}
        />
      )}

      {config.recipe !== null && (
        <Box bg={'gray-100'}>
          <DataSetCompositeRecipeConfigurationComponent
            taggedDataSetReferences={config.taggedDataSetReferences}
            organizationId={organizationId}
            databaseId={databaseId}
            recipe={config.recipe}
            setRecipe={setRecipe}
            generateInsightsPayload={generateReportsInsightsPayload}
          />
        </Box>
      )}

      {config.recipe === null && (
        <>
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => setPreloadableCode(),
            }}
            text={'Preload Composite Dataset Code'}
          />

          <TransformEditorUI
            code={config.pythonScriptContents}
            setCode={setCode}
            isRunningExecution={false}
            isLoadingScript={false}
            runOnBatch={false}
            setRunOnBatch={() => {}}
            transformType={'python'}
            setTransformType={() => {}}
            platformTransformDetails={null}
            setPlatformTransformDetails={() => {}}
            isToBeCompared={false}
            setIsToBeCompared={() => {}}
            entitledDagsterTransforms={[]}
            selectedDagsterTransformId={null}
            setSelectedDagsterTransformId={() => {}}
          />
        </>
      )}

      {modal}
    </Spacing>
  );
};
